import { ArrowDownIcon, ArrowLeftIcon, ArrowRightIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent } from 'react';
import { Button } from './Button';

// DirectionalPad — a 4-way up/down/left/right control for relocating a
// selected item within a fixed spatial layout (story 4-88b; P06 §4, P10 §5,
// DOD §1.7 rule 7). First consumer: `ReportDesigner.tsx`'s letterhead panel,
// moving the active `SlotGridPicker` cell's content to an adjacent named
// slot — see that file for the actual "what does a move mean" logic, which
// this primitive deliberately knows nothing about (it only reports which
// direction was pressed).
//
// ── WHY A NEW PRIMITIVE, not a one-off ──────────────────────────────────────
// `OrderedListField` (components/ui) already has a directional move (up/
// down only, one item at a time in a 1D list) — checked, and its own header
// comment documents exactly why that a11y shape (real `Button`s, row-
// qualified accessible names, boundary buttons DISABLED not hidden) is
// correct. This primitive reuses that same shape for FOUR directions instead
// of two, because a 2D grid position needs left/right too. Composing the
// existing `Button` primitive (not a new bespoke `<button>`) mirrors
// `OrderedListField`'s own composition — consistent with G-22 (DS-primitive
// composition) two levels deep.
//
// ── A11Y (P10 §2, D44) ──────────────────────────────────────────────────────
//  * Four real `Button`s (`variant="ghost" iconOnly`), so Tab + Enter/Space
//    operates every direction without any custom keydown handling — ground
//    rule: "keyboard-operable directional control, NOT mouse-only".
//  * Each button's accessible name is CONTEXT-QUALIFIED ("Move Logo — left
//    up") via the required `aria-label` prop, the same "row-qualified name"
//    reasoning `OrderedListField` documents (four identical "Move up"
//    buttons across re-renders/screens are indistinguishable otherwise).
//  * A direction with no valid target is DISABLED (`disabled` prop), never
//    removed — a control that disappears mid-interaction moves focus
//    somewhere unexpected (same boundary rule `OrderedListField` uses at the
//    first/last row).
//  * BONUS keyboard path: arrow keys anywhere inside the pad's wrapper also
//    fire the matching direction (`onKeyDown` below), for a user who tabs
//    once into the pad and then arrows rather than re-tabbing between four
//    buttons. This is additive to the Tab+Enter path above, never a
//    replacement for it.

export type DirectionalPadDirection = 'up' | 'down' | 'left' | 'right';

export interface DirectionalPadProps {
  onMove: (direction: DirectionalPadDirection) => void;
  /** Per-direction disable — the caller computes this from its own spatial
   * adjacency (e.g. `findAdjacentSlot` returning `null`). */
  disabled?: Partial<Record<DirectionalPadDirection, boolean>>;
  /** What is being moved, e.g. "Logo — left" — combined into each button's
   * own accessible name ("Move Logo — left up") and used to name the pad's
   * `role="group"`. Required: an unqualified pad is four indistinguishable
   * "Move up"/"Move down"/… buttons to a screen reader. */
  contextLabel: string;
  className?: string;
}

const DIRECTION_ICON: Record<DirectionalPadDirection, JSX.Element> = {
  up: <ArrowUpIcon className="h-4 w-4" />,
  down: <ArrowDownIcon className="h-4 w-4" />,
  left: <ArrowLeftIcon className="h-4 w-4" />,
  right: <ArrowRightIcon className="h-4 w-4" />,
};

const DIRECTION_GRID_POSITION: Record<DirectionalPadDirection, { gridRow: number; gridColumn: number }> = {
  up: { gridRow: 1, gridColumn: 2 },
  left: { gridRow: 2, gridColumn: 1 },
  right: { gridRow: 2, gridColumn: 3 },
  down: { gridRow: 3, gridColumn: 2 },
};

const KEY_TO_DIRECTION: Record<string, DirectionalPadDirection> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

export function DirectionalPad({ onMove, disabled = {}, contextLabel, className = '' }: DirectionalPadProps): JSX.Element {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const direction = KEY_TO_DIRECTION[event.key];
    if (!direction || disabled[direction]) return;
    event.preventDefault();
    onMove(direction);
  };

  return (
    <div
      role="group"
      aria-label={`Relocate ${contextLabel}`}
      onKeyDown={handleKeyDown}
      className={`grid w-fit grid-rows-3 grid-cols-3 gap-1 ${className}`}
    >
      {(Object.keys(DIRECTION_GRID_POSITION) as DirectionalPadDirection[]).map((direction) => (
        <Button
          key={direction}
          variant="ghost"
          size="sm"
          iconOnly
          icon={DIRECTION_ICON[direction]}
          disabled={disabled[direction]}
          onClick={() => onMove(direction)}
          style={DIRECTION_GRID_POSITION[direction]}
        >
          {`Move ${contextLabel} ${direction}`}
        </Button>
      ))}
    </div>
  );
}
